import { appConstants } from "@abp";
import { ConsentChoice, consentSignals, readConsent } from "./consent";

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

/** Tracking is off when no container id is configured (local dev, preview deploys). */
export const isTrackingEnabled = () => Boolean(appConstants.gtmId);

// gtag() must push the `arguments` object itself; GTM ignores a plain array
function gtag(..._args: unknown[]) {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export const updateConsent = (choice: ConsentChoice) => {
  if (!isTrackingEnabled()) return;
  gtag("consent", "update", consentSignals(choice));
};

/**
 * Loads Google Tag Manager with Consent Mode v2. Everything starts denied, so no
 * tag that respects consent (the Meta pixel is set up that way in GTM) fires
 * before the visitor accepts.
 */
export const initGtm = () => {
  const { gtmId } = appConstants;

  if (!gtmId || document.getElementById("gtm-script")) return;

  window.dataLayer = window.dataLayer || [];

  gtag("consent", "default", { ...consentSignals("denied"), wait_for_update: 500 });

  const stored = readConsent();
  if (stored) gtag("consent", "update", consentSignals(stored));

  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const script = document.createElement("script");
  script.id = "gtm-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);
};

/** Pushes a custom event to the data layer; GTM decides which tags react to it. */
export const trackEvent = (event: string, params: Record<string, unknown> = {}) => {
  if (!isTrackingEnabled()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
};
